import React, { useCallback, Fragment } from 'react';
import { useQuery } from '@apollo/react-hooks';
import type { QueryResult } from 'react-apollo';

import { DatabasePrerequisites } from '@confluence/content-prerequisites';
import { Attribution } from '@confluence/error-boundary';
import { DATABASE_VIEW } from '@confluence/named-routes';
import { usePubSubEvent } from '@confluence/pubsub-client';
import { useSearchSessionId } from '@confluence/search-session';
import {
	ContentTypeDwellTimeComponent,
	useIsStandardizedDwellEnabled,
} from '@confluence/content-types-utils';
import { useBooleanFeatureFlag } from '@confluence/session-data';

import { DatabaseShell } from './DatabaseShell';
import { DatabaseUnifiedQuery } from './graphql/DatabaseUnifiedQuery.graphql';
import type {
	DatabaseUnifiedQuery as DatabaseUnifiedQueryType,
	DatabaseUnifiedQueryVariables,
} from './graphql/__types__/DatabaseUnifiedQuery';

type DatabaseContainerType = {
	contentId: string;
	spaceKey: string;
};

export type DatabaseUnifiedQueryResultType = Pick<
	QueryResult<DatabaseUnifiedQueryType>,
	'data' | 'error' | 'loading'
>;

const DatabaseContainerComponent = ({ contentId, spaceKey }: DatabaseContainerType) => {
	const [{ searchSessionId, additionalAnalytics }] = useSearchSessionId();
	const isSpaceAliasFFEnabled = useBooleanFeatureFlag('confluence.frontend.space.alias');
	const areDwellEventsStandardized = useIsStandardizedDwellEnabled();

	const { networkStatus, refetch, ...databaseUnifiedQueryResult } = useQuery<
		DatabaseUnifiedQueryType,
		DatabaseUnifiedQueryVariables
	>(DatabaseUnifiedQuery, {
		variables: {
			contentId,
			spaceKey,
			includeAlias: isSpaceAliasFFEnabled,
			// The DSP query doesn't currently user the content version value, so using a fixed value
			contentVersion: 1,
		},
		notifyOnNetworkStatusChange: true,
		context: { single: true },
	});

	const { data, loading } = databaseUnifiedQueryResult;

	const content = data?.content?.nodes || [];
	const contentReferenceId = content[0]?.referenceId;
	const spaceId = content[0]?.space?.id;
	const spaceType = content[0]?.space?.type;
	const spaceName = content[0]?.space?.name;
	const title = content[0]?.title;
	const isUserViewingOwnPersonalSpace = data?.user?.confluence?.space?.id === spaceId;
	const identity = {
		accountId: data?.user?.id,
		nickname: data?.user?.displayName,
		picture: data?.user?.photos?.find((photo) => photo?.isPrimary)?.value,
		email: data?.user?.emails?.find((email) => email?.isPrimary)?.value,
		locale: data?.user?.locale,
	};

	const onEventHandler = useCallback(() => {
		void refetch();
	}, [refetch]);

	usePubSubEvent({
		contentId,
		contentType: 'database',
		eventName: `restrictionUpdated:database`,
		attribution: Attribution.CONTENT_TYPES,
		onEvent: onEventHandler,
	});

	return (
		<Fragment>
			{!loading && !areDwellEventsStandardized && (
				<ContentTypeDwellTimeComponent
					contentType="database"
					contentId={contentId}
					spaceId={spaceId}
					contentReferenceId={contentReferenceId}
					searchSessionId={searchSessionId}
					spaceType={spaceType}
					isUserViewingOwnPersonalSpace={isUserViewingOwnPersonalSpace}
					iframeDwell
					additionalAnalytics={additionalAnalytics}
				/>
			)}
			<DatabasePrerequisites route={DATABASE_VIEW} contentQueryResult={databaseUnifiedQueryResult}>
				<DatabaseShell
					spaceKey={spaceKey}
					contentId={contentId}
					contentReferenceId={contentReferenceId}
					loading={loading}
					spaceId={spaceId}
					queryNetworkStatus={networkStatus}
					spaceName={spaceName}
					title={title}
					identity={identity}
				/>
			</DatabasePrerequisites>
		</Fragment>
	);
};

export const DatabaseContainer = DatabaseContainerComponent;
